const API_BASE_URL = process.env.API_BASE_URL || 'http://localhost:8000/api/v1';

type RequestOptions = RequestInit & {
  token?: string | null;
  skipAuthRefresh?: boolean;
};

type AccessTokenRefreshHandler = () => Promise<string | null>;

let accessTokenRefreshHandler: AccessTokenRefreshHandler | null = null;
let accessTokenRefreshPromise: Promise<string | null> | null = null;

export function setAccessTokenRefreshHandler(handler: AccessTokenRefreshHandler | null) {
  accessTokenRefreshHandler = handler;

  return () => {
    if (accessTokenRefreshHandler === handler) {
      accessTokenRefreshHandler = null;
    }
  };
}

export class ApiError extends Error {
  status: number;
  code?: string;
  detail?: unknown;

  constructor(message: string, status: number, options?: { code?: string; detail?: unknown }) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = options?.code;
    this.detail = options?.detail;
  }
}

export function buildApiUrl(path: string): string {
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

function buildRequestHeaders(options: RequestOptions): Headers {
  const headers = new Headers(options.headers);
  const isFormDataBody = typeof FormData !== 'undefined' && options.body instanceof FormData;

  if (options.body && !isFormDataBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (options.token) {
    headers.set('Authorization', `Bearer ${options.token}`);
  }

  return headers;
}

async function refreshAccessToken(): Promise<string | null> {
  if (!accessTokenRefreshHandler) {
    return null;
  }

  if (!accessTokenRefreshPromise) {
    accessTokenRefreshPromise = accessTokenRefreshHandler()
      .finally(() => {
        accessTokenRefreshPromise = null;
      });
  }

  return accessTokenRefreshPromise;
}

async function fetchWithSessionRetry(url: string, options: RequestOptions): Promise<Response> {
  const fetchOptions: RequestOptions = { ...options };
  delete fetchOptions.token;
  delete fetchOptions.skipAuthRefresh;
  const response = await fetch(url, {
    ...fetchOptions,
    headers: buildRequestHeaders(options),
  });

  if (
    response.status !== 401 ||
    !options.token ||
    options.skipAuthRefresh ||
    !accessTokenRefreshHandler
  ) {
    return response;
  }

  const refreshedAccessToken = await refreshAccessToken();
  if (!refreshedAccessToken) {
    return response;
  }

  const retryOptions = { ...options, token: refreshedAccessToken };
  const retryFetchOptions: RequestOptions = { ...retryOptions };
  delete retryFetchOptions.token;
  delete retryFetchOptions.skipAuthRefresh;
  return fetch(url, {
    ...retryFetchOptions,
    headers: buildRequestHeaders(retryOptions),
  });
}

export function extractApiError(responseBody: unknown, responseStatus: number): ApiError {
  const detail =
    typeof responseBody === 'object' && responseBody !== null && 'detail' in responseBody
      ? responseBody.detail
      : responseBody;

  let message = 'Request failed.';
  let code: string | undefined;

  if (typeof detail === 'string') {
    message = detail;
  } else if (detail && typeof detail === 'object') {
    if ('message' in detail && typeof detail.message === 'string') {
      message = detail.message;
    }
    if ('error_code' in detail && typeof detail.error_code === 'string') {
      code = detail.error_code;
    }
  }

  return new ApiError(message, responseStatus, { code, detail });
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = buildApiUrl(path);
  const response = await fetchWithSessionRetry(url, options);

  const contentType = response.headers.get('content-type') || '';
  const responseBody = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    throw extractApiError(responseBody, response.status);
  }

  return responseBody as T;
}

export async function requestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const url = buildApiUrl(path);
  const response = await fetchWithSessionRetry(url, options);

  if (!response.ok) {
    const contentType = response.headers.get('content-type') || '';
    const responseBody = contentType.includes('application/json')
      ? await response.json()
      : await response.text();

    throw extractApiError(responseBody, response.status);
  }

  return response.blob();
}
